import { apiFetch } from './api';

export interface PersonListItem {
  id: string;
  displayName: string | null;
  username: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  /** Primary TELEGRAM_ID identity (зливається з person_identities). */
  telegramId: string | null;
  currentSubscriptionStatus: string | null;
  currentTariff: string | null;
  subscriptionEndsAt: string | null;
  ltvCents: number;
  paymentCount: number;
  lastSeenAt: string;
  firstSeenAt: string;
  /** Custom-поля (schema у CustomDataField з tableSlug='users'). */
  customFields?: Record<string, unknown>;
}

export interface IdentityValue {
  id: string;
  kind: string;
  value: string;
  isPrimary: boolean;
  source: string;
  firstSeenAt: string;
}

export interface PersonDetail extends PersonListItem {
  identities: IdentityValue[];
  identitiesByKind: Record<string, IdentityValue[]>;
  funnels: { slug: string; name: string; reachedStages: number; totalStages: number }[];
  paymentsCount: number;
  subscriptionsCount: number;
  materialsCount: number;
  favoritesCount: number;
  customFields: Record<string, unknown>;
}

/** Створити «голий» контакт (inline-create у relation-picker'і). */
export function createPerson(displayName: string): Promise<{ id: string }> {
  return apiFetch('/persons', { method: 'POST', body: { displayName } });
}

export function addIdentity(
  personId: string,
  kind: string,
  value: string,
): Promise<{ identityId: string; isPrimary: boolean; wasExisting: boolean }> {
  return apiFetch(`/persons/${personId}/identities`, {
    method: 'POST',
    body: { kind, value },
  });
}
export function removeIdentity(personId: string, identityId: string): Promise<unknown> {
  return apiFetch(`/persons/${personId}/identities/${identityId}`, { method: 'DELETE' });
}
export function promoteIdentity(personId: string, identityId: string): Promise<unknown> {
  return apiFetch(`/persons/${personId}/identities/${identityId}/primary`, { method: 'POST' });
}

export interface TimelineRow {
  id: string;
  type: string;
  occurredAt: string;
  source: string;
  props: Record<string, unknown>;
  funnelSlug: string | null;
  stageSlug: string | null;
}

/** Платіж конкретного Person — для "Платежі" таб у PersonDetail (Phase 2). */
export interface PersonPaymentRow {
  id: string;
  occurredAt: string;
  amountCents: number;
  currency: string;
  status: string;
  externalRef: string | null;
  rawTariff: string | null;
  productSku: string | null;
  productName: string | null;
  paymentMethod: string | null;
  cardLast4: string | null;
  inferred: boolean;
}

export function fetchPersonPayments(personId: string): Promise<{
  items: PersonPaymentRow[];
  total: number;
}> {
  return apiFetch(`/persons/${personId}/payments`);
}

export interface PaymentRow {
  id: string;
  occurredAt: string;
  amountCents: number;
  currency: string;
  status: string;
  externalRef: string | null;
  inferred: boolean;
  rawTariff: string | null;
  productSku: string | null;
  /** null — контакт видалено, платіж відв'язаний. */
  person: {
    id: string;
    displayName: string | null;
    primaryEmail: string | null;
    primaryPhone: string | null;
  } | null;
  /** Custom-поля (schema у CustomDataField з tableSlug='payments'). */
  customFields?: Record<string, unknown>;
}

export function fetchPersons(opts: {
  status?: string;
  tariff?: string;
  search?: string;
  minLtvCents?: number;
  maxLtvCents?: number;
  minPayments?: number;
  maxPayments?: number;
  firstSeenFrom?: string; // YYYY-MM-DD
  firstSeenTo?: string;
  lastSeenFrom?: string;
  lastSeenTo?: string;
  hasTelegram?: boolean;
  hasEmail?: boolean;
  hasPhone?: boolean;
  /** Built-in: <col>Desc/Asc. Custom: custom:<fieldKey>:asc|desc (JSONB path sort). */
  sort?:
    | 'firstSeenDesc'
    | 'firstSeenAsc'
    | 'ltvDesc'
    | 'ltvAsc'
    | 'paymentsDesc'
    | 'paymentsAsc'
    | `custom:${string}:asc`
    | `custom:${string}:desc`;
  filter?: string; // JSON-stringified filter tree
  page?: number;
  pageSize?: number;
}): Promise<{ items: PersonListItem[]; total: number; page: number; pageSize: number }> {
  const p = new URLSearchParams();
  if (opts.status) p.set('status', opts.status);
  if (opts.tariff) p.set('tariff', opts.tariff);
  if (opts.search) p.set('search', opts.search);
  if (opts.minLtvCents !== undefined) p.set('minLtvCents', String(opts.minLtvCents));
  if (opts.maxLtvCents !== undefined) p.set('maxLtvCents', String(opts.maxLtvCents));
  if (opts.minPayments !== undefined) p.set('minPayments', String(opts.minPayments));
  if (opts.maxPayments !== undefined) p.set('maxPayments', String(opts.maxPayments));
  if (opts.firstSeenFrom) p.set('firstSeenFrom', opts.firstSeenFrom);
  if (opts.firstSeenTo) p.set('firstSeenTo', opts.firstSeenTo);
  if (opts.lastSeenFrom) p.set('lastSeenFrom', opts.lastSeenFrom);
  if (opts.lastSeenTo) p.set('lastSeenTo', opts.lastSeenTo);
  if (opts.hasTelegram !== undefined) p.set('hasTelegram', String(opts.hasTelegram));
  if (opts.hasEmail !== undefined) p.set('hasEmail', String(opts.hasEmail));
  if (opts.hasPhone !== undefined) p.set('hasPhone', String(opts.hasPhone));
  if (opts.sort) p.set('sort', opts.sort);
  if (opts.filter) p.set('filter', opts.filter);
  if (opts.page) p.set('page', String(opts.page));
  if (opts.pageSize) p.set('pageSize', String(opts.pageSize));
  return apiFetch(`/persons?${p.toString()}`);
}

export function fetchPersonDetail(id: string): Promise<PersonDetail> {
  return apiFetch(`/persons/${id}`);
}

export function fetchTimeline(
  id: string,
  cursor?: string,
): Promise<{ items: TimelineRow[]; nextCursor: string | null }> {
  const p = new URLSearchParams();
  if (cursor) p.set('cursor', cursor);
  return apiFetch(`/persons/${id}/timeline?${p.toString()}`);
}

export interface JourneySource {
  type: 'webhook' | 'import' | 'automation' | 'manual' | 'unknown';
  label: string;
  detail?: string;
  link?: string;
  at?: string;
}

export type JourneyItem =
  | {
      kind: 'first_seen';
      occurredAt: string;
      title: string;
      source?: JourneySource;
    }
  | {
      kind: 'payment';
      occurredAt: string;
      title: string;
      subtitle: string | null;
      status: string;
      amountCents: number;
      paymentId: string;
      productSku: string | null;
      source?: JourneySource;
    }
  | {
      kind: 'automation_run';
      occurredAt: string;
      title: string;
      subtitle: string | null;
      automationId: string;
      runId: string;
      status: string;
      source?: JourneySource;
    };

export function fetchPersonJourney(id: string): Promise<{ items: JourneyItem[] }> {
  return apiFetch(`/persons/${id}/journey`);
}

export function fetchPayments(opts: {
  status?: string;
  rangeDays?: number;
  search?: string;
  productSku?: string;
  minCents?: number;
  inferredOnly?: boolean;
  unmatchedOnly?: boolean;
  sort?:
    | 'occurredDesc'
    | 'occurredAsc'
    | 'amountDesc'
    | 'amountAsc'
    | `custom:${string}:asc`
    | `custom:${string}:desc`;
  filter?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ items: PaymentRow[]; total: number; page: number; pageSize: number }> {
  const p = new URLSearchParams();
  if (opts.status) p.set('status', opts.status);
  p.set('rangeDays', String(opts.rangeDays ?? 3650)); // дефолт "усі дані"
  if (opts.search) p.set('search', opts.search);
  if (opts.productSku) p.set('productSku', opts.productSku);
  if (opts.minCents !== undefined) p.set('minCents', String(opts.minCents));
  if (opts.inferredOnly) p.set('inferredOnly', 'true');
  if (opts.unmatchedOnly) p.set('unmatchedOnly', 'true');
  if (opts.sort) p.set('sort', opts.sort);
  if (opts.filter) p.set('filter', opts.filter);
  if (opts.page) p.set('page', String(opts.page));
  if (opts.pageSize) p.set('pageSize', String(opts.pageSize));
  return apiFetch(`/payments?${p.toString()}`);
}

export interface PaymentSummary {
  rangeDays: number;
  grossCents: number;
  refundCents: number;
  netCents: number;
  feesCents: number;
  netAfterFeesCents: number;
  aovCents: number;
  count: number;
  unmatchedCount: number;
}

/**
 * KPI-summary тепер приймає ті ж самі фільтри що й fetchPayments — щоб
 * картки Gross/Refunds/Net/AOV перераховувались по фільтрованій вибірці.
 */
export function fetchPaymentSummary(opts: {
  status?: string;
  rangeDays?: number;
  search?: string;
  productSku?: string;
  minCents?: number;
  inferredOnly?: boolean;
  unmatchedOnly?: boolean;
  filter?: string;
} = {}): Promise<PaymentSummary> {
  const p = new URLSearchParams();
  p.set('rangeDays', String(opts.rangeDays ?? 3650));
  if (opts.status) p.set('status', opts.status);
  if (opts.search) p.set('search', opts.search);
  if (opts.productSku) p.set('productSku', opts.productSku);
  if (opts.minCents !== undefined) p.set('minCents', String(opts.minCents));
  if (opts.inferredOnly) p.set('inferredOnly', 'true');
  if (opts.unmatchedOnly) p.set('unmatchedOnly', 'true');
  if (opts.filter) p.set('filter', opts.filter);
  return apiFetch(`/payments/summary?${p.toString()}`);
}
